/**
 * RS-2b — "Ask the designer": the three fields, wherever the question is asked
 * from. The punch row and the tag page open it in the app's dock; the phone
 * widget has its own screen-sized version.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DockPanel from './shell/DockPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const askWord = (status) => (
  status === 'responded' ? 'Answered' : status === 'closed' ? 'Closed' : 'Waiting');

/** The micro label a row wears once a question has been asked on it. */
export const AskedLabel = ({ rfi, projectId, testid }) => (
  <a
    href={`/projects/${projectId}/rfis?rfi=${rfi.id}`}
    onClick={(e) => e.stopPropagation()}
    className="font-mono text-xs hover:underline"
    data-testid={testid}
  >
    {`${rfi.rfi_number} · ${askWord(rfi.status)}`}
  </a>
);

export default function AskDesignerSheet({ projectId, title, url, onAsked, onClose }) {
  const [question, setQuestion] = useState(title || '');
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState('');
  const [photos, setPhotos] = useState(true);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/people-who-answer`);
      setPeople(data.people || []);
      setWho(data.default_user_id || (data.people || [])[0]?.id || '');
    } catch (_e) { setProblem('Could not read who can answer'); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!question.trim()) { setProblem('Write the question first'); return; }
    if (!who) { setProblem('Say who answers it'); return; }
    setSending(true);
    setProblem('');
    try {
      const { data } = await axios.post(`${API}${url}`, {
        question: question.trim(), assigned_to: who, include_photos: photos,
      });
      onAsked(data);
      onClose();
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That question did not send');
    }
    setSending(false);
  };

  return (
    <DockPanel onClose={onClose} width="420px">
      <aside className="kmt-panel kst-panel" data-testid="ask-designer-sheet">
        <div className="kp-head"><span className="kst-k">Ask the designer</span></div>
        <div className="kst-form">
          <label className="kst-f">
            <span>The question</span>
            <textarea className="in" rows={4} maxLength={2000} value={question}
              onChange={(e) => setQuestion(e.target.value)}
              data-testid="ask-designer-question" />
          </label>
          <label className="kst-f">
            <span>Who answers</span>
            <select className="in" value={who} onChange={(e) => setWho(e.target.value)}
              data-testid="ask-designer-who">
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="kst-f" data-testid="ask-designer-photos">
            <span>Include the photos</span>
            <input type="checkbox" checked={photos}
              onChange={(e) => setPhotos(e.target.checked)} />
          </label>
          {problem ? <p className="kst-err" data-testid="ask-designer-error">{problem}</p> : null}
          <div className="kstp-acts">
            <button type="button" className="kst-btn kst-btn--brand"
              disabled={sending || !people.length}
              onClick={send} data-testid="ask-designer-send">
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button type="button" className="kst-btn" onClick={onClose}
              data-testid="ask-designer-cancel">Never mind</button>
          </div>
        </div>
      </aside>
    </DockPanel>
  );
}
